import React, { useContext, useEffect, useRef, useState } from 'react';
import qs from 'query-string';
import { ThemeProvider } from 'styled-components';
import { Outlet } from 'react-router-dom';
import { isEmpty, mergeDeepRight } from 'ramda';
import { useSetRecoilState } from 'recoil';
import FrameSizeContext from '~/widget/context/FrameSizeContext';
import HeightContext from '~/widget/context/HeightContext';
import HostInfoContext from '~/widget/context/HostInfoContext';
import TrackingContext, {
  DEFAULT_TRACKING_CONTEXT_VALUE,
} from '~/widget/context/TrackingContext';
import WidgetContext, { IWidgetContext } from '~/widget/context/WidgetContext';
import useWidgetContext from '~/widget/hooks/useWidgetContext';
import useLocalStorageValue from '@bndl-io/use-local-storage';

import useFireTrackingEvent from '~/widget/hooks/useFireTrackingEvent';
import useSetupAnalytics from '~/widget/hooks/useSetupAnalytics';
import {
  ConsentParams,
  FrameSizeSelector,
  HostInfo,
  WindowConfig,
  Locale,
} from '~/shared/types';
import useMessaging from '~/widget/hooks/useMessaging';
import normalizeConsentParams from '~/shared/utils/normalizeConsentParams';
import windowConfigToWidgetContext from '~/widget/utils/windowConfigToWidgetContext';
import getTheme from '~/widget/theme';
import IngestParamsProvider from '../IngestParamsProvider';
import getStoredLocale from '~/widget/utils/getStoredLocale';
import getBrowserLocale from '~/widget/utils/getBrowserLocale';
import {
  consentSelector,
  hostPageDimensionsState,
  localeSelector,
} from '~/widget/state/widgetState';
import consentParamsToCategories from '../CookieBanner/utils/consentParamsToCategories';
import syncConsentCategoriesToBanner from '../CookieBanner/utils/syncConsentCategoriesToBanner';
import { DEFAULT_CONSENT_OPTIONS } from '~/shared/constants/consent';
import isMobile from '~/widget/utils/isMobile';
import generateBntrk, { extendBntrk } from '~/shared/bntrk';
import encodeBntrk from '~/shared/bntrk/encode';
import getWidgetMode from '~/shared/utils/getWidgetMode';

import { mobileBreakpoint } from '~/shared/constants/iframeSizes';

export const AppWrapper: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const widgetContext = useWidgetContext();

  const headerRef = useRef<HTMLHRElement | null>(null);
  const footerRef = useRef<HTMLHRElement | null>(null);

  return (
    <IngestParamsProvider>
      <TrackingContext.Provider
        value={{
          ...DEFAULT_TRACKING_CONTEXT_VALUE,
          accountId: widgetContext.accountId,
        }}
      >
        <WithAnalytics>
          <WithSessionStart>
            <HeightContext.Provider value={{ footerRef, headerRef }}>
              <ThemeProvider theme={getTheme(widgetContext.styleConfig)}>
                {children}
              </ThemeProvider>
            </HeightContext.Provider>
          </WithSessionStart>
        </WithAnalytics>
      </TrackingContext.Provider>
    </IngestParamsProvider>
  );
};

const WithAnalytics: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const widgetContext = useWidgetContext();
  useSetupAnalytics(widgetContext);

  return <>{children}</>;
};

const WithSessionStart: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const sessionStarted = useRef(false);
  const { sessionId } = useContext(TrackingContext);
  const fireTrackingEvent = useFireTrackingEvent();

  useEffect(() => {
    if (sessionStarted.current === false) {
      fireTrackingEvent({
        event: 'SessionStart',
        sessionId: sessionId,
      });
      sessionStarted.current = true;
    }
  }, [fireTrackingEvent, sessionId]);

  return <>{children}</>;
};

export const BNDL_COOKIE_CONSENT_STORAGE_KEY = '__bndl_consent_params__';
export const WithConfig: React.FC = ({ ...props }) => {
  const setConsentParamsInState = useSetRecoilState(consentSelector);

  const [consentParams, setConsentParams] = useLocalStorageValue<ConsentParams>(
    BNDL_COOKIE_CONSENT_STORAGE_KEY,
  );

  const [widgetContext, setWidgetContext] = useState<IWidgetContext | null>(
    null,
  );
  const setLocale = useSetRecoilState(localeSelector);

  const [frameSizeSelector, setFrameSizeSelector] =
    useState<FrameSizeSelector>('desktop');

  const setHostPageDimensions = useSetRecoilState(hostPageDimensionsState);

  const getFrameSizeSelector = (width: number) =>
    width > mobileBreakpoint ? 'desktop' : 'mobile';

  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);

  const sendMessage = useMessaging((message) => {
    switch (message.type) {
      case 'Bootstrap_PageDimensions':
        setFrameSizeSelector(getFrameSizeSelector(message.payload.width));
        setHostPageDimensions(message.payload);
        break;

      case 'sendConsent': {
        const normalizedConsentParams = normalizeConsentParams(
          message.payload as ConsentParams,
        );
        setConsentParams(normalizedConsentParams);
        const categories = consentParamsToCategories(normalizedConsentParams);
        syncConsentCategoriesToBanner(categories);
        break;
      }

      case 'overwriteConfig': {
        // Only needed for TS, the config object should be there.
        const newConfig =
          window.__DH_CONFIG != null
            ? (mergeDeepRight<WindowConfig, Partial<WindowConfig>>(
                window.__DH_CONFIG,
                message.payload,
              ) as WindowConfig)
            : window.__DH_CONFIG;

        // Also set on window
        window.__DH_CONFIG = newConfig;
        setWidgetContext(
          windowConfigToWidgetContext({
            ...widgetContext,
            windowConfig: newConfig,
            hostPageParams: widgetContext?.hostPageParams,
          }),
        );
        break;
      }

      case 'hostInfo':
        setHostInfo(message.payload);
        break;

      default:
        break;
    }
  });

  /**
   * Initial setup of the widget
   *
   * Parsing DH_CONFIG, language etc.
   */
  useEffect(() => {
    const search = global.window.location.search;
    const {
      bndl_height,
      bndl_width,
      preventDefaultOpening,
      bndl_host_info,
      bndl_locale,
      bntrk,
    } = qs.parse(search);

    const _preventDefaultOpening: boolean =
      typeof preventDefaultOpening === 'string'
        ? Boolean(parseInt(preventDefaultOpening))
        : false;

    const _hostPageDimensions = {
      width: Number(bndl_width ?? 0),
      height: Number(bndl_height ?? 0),
    };

    setHostPageDimensions(_hostPageDimensions);

    if (typeof bndl_host_info === 'string') {
      try {
        setHostInfo(JSON.parse(bndl_host_info));
      } catch {
        // Ignore malformed host info
      }
    }

    setFrameSizeSelector(
      (() => {
        if (bndl_width == null) return isMobile() ? 'mobile' : 'desktop';
        const width = Number(bndl_width);
        if (isNaN(width)) return isMobile() ? 'mobile' : 'desktop';
        return getFrameSizeSelector(width);
      })(),
    );

    const bntrkParam = (() => {
      if (getWidgetMode() === 'iframe') return bntrk as string | null;

      const bntrkData = extendBntrk(
        generateBntrk(new URLSearchParams(search), true),
        { 'dh:host': 'standalone' },
      );

      if (isEmpty(bntrkData)) return null;

      return encodeBntrk(bntrkData);
    })();

    setWidgetContext(
      windowConfigToWidgetContext({
        windowConfig: window.__DH_CONFIG,
        hostPageParams: search,
        bntrk: bntrkParam,
        preventDefaultOpening: _preventDefaultOpening,
      }),
    );

    const explicitLocale =
      typeof bndl_locale === 'string' ? (bndl_locale as Locale) : null;

    setLocale(
      explicitLocale ??
        getStoredLocale(window.__DH_CONFIG.accountId) ??
        getBrowserLocale(),
    );

    // eslint-disable-next-line no-console
    if (window.__DH_CONFIG?.apps.length === 0 && console && console.warn) {
      // eslint-disable-next-line no-console
      console.warn(`[BNDL Widget] No apps enabled, please enable some apps.`);
    }
  }, []);

  /**
   * Watch for consent updates
   */
  useEffect(() => {
    const normalizedConsentParams = consentParams ?? DEFAULT_CONSENT_OPTIONS;
    setConsentParamsInState(normalizedConsentParams);

    setWidgetContext((prev) => {
      if (prev !== null) {
        return { ...prev, consentParams: normalizedConsentParams };
      }

      return prev;
    });
  }, [consentParams]);

  /** Sent once when we first have config, so bootstrap can show the iframe. Never re-send on context updates (e.g. overwriteConfig) to avoid a loop. */
  const hasSentConfigLoadedRef = useRef(false);

  useEffect(() => {
    if (widgetContext === null || hasSentConfigLoadedRef.current) return;

    hasSentConfigLoadedRef.current = true;

    sendMessage({
      type: 'Widget_ConfigLoaded',
      payload: { windowConfig: window.__DH_CONFIG },
    });
  }, [sendMessage, widgetContext, frameSizeSelector]);

  /** Widget not yet loaded - or we do not show anything without apps */
  if (widgetContext === null) return null;
  if (Object.keys(widgetContext?.apps).length === 0) return null;

  return (
    <FrameSizeContext.Provider value={frameSizeSelector}>
      <HostInfoContext.Provider value={hostInfo}>
        <WidgetContext.Provider value={widgetContext}>
          <>
            <AppWrapper {...props}>
              <Outlet />
            </AppWrapper>
          </>
        </WidgetContext.Provider>
      </HostInfoContext.Provider>
    </FrameSizeContext.Provider>
  );
};

export default WithConfig;
